import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../services/api-client';
import { ApiError } from '../services/errors';
import type { AuthenticatedUser } from '../types/api';

export const sessionKey = ['session'] as const;

export interface SessionState {
  user: AuthenticatedUser | null;
}

/**
 * Sessão corrente. A ausência de sessão é um estado normal da aplicação, não um
 * erro: `401` vira `user: null` para que o roteador redirecione sem que a tela
 * de login pisque uma mensagem de falha.
 */
export function useSession() {
  return useQuery({
    queryKey: sessionKey,
    queryFn: async (): Promise<SessionState> => {
      try {
        const response = await apiRequest<{ user: AuthenticatedUser }>('/me');
        return { user: response.user };
      } catch (error) {
        if (ApiError.is(error) && error.isUnauthenticated) return { user: null };
        throw error;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: { email: string; password: string }) =>
      apiRequest<{ user: AuthenticatedUser }>('/auth/login', {
        method: 'POST',
        body: credentials,
      }),
    onSuccess: (response) => {
      queryClient.setQueryData<SessionState>(sessionKey, { user: response.user });
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiRequest<void>('/auth/logout', { method: 'POST' }),
    onSettled: () => {
      // Limpa tudo, não só a sessão: o cache guarda pesquisas e resultados do
      // usuário que acabou de sair.
      queryClient.clear();
      queryClient.setQueryData<SessionState>(sessionKey, { user: null });
    },
  });
}
